import { useState, useRef, useMemo, useEffect, MouseEventHandler } from "react";
// Helpers
import { getFormatAmount } from "@/Common/helpers/number";
import { errorLogger, getError } from "@/Common/helpers/logger";
import { getCellClassNameFn, isEmptyRow, isFooterCell } from "@/Common/helpers/Tables";
// Components
import AddCellIssues from "@/Layouts/components/Tables/DataSmartTable/additional/AddCellIssues";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import TableLoader from "@/Layouts/components/Skeletons/TableLoader";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import { Box, Stack, Tooltip } from "@mui/material";

// Materials
import { GridCellProps, GridColDef, GridValidRowModel, useGridApiRef } from "@mui/x-data-grid-premium";
// Types and constants
import { Issue } from "@/Common/types/tables";
import { HEADER_CLASS_NAME, ID_GRID_COLDEF } from "@/Washouts/constants";
import { CallActivityPerformanceProps } from "@/Washouts/types/commons";
// Icons
import InfoIcon from "@Washouts/assets/icons/info-grey.svg";

function getColumns(issues: Issue[], percents: CallActivityPerformanceProps["percents"], issuesReference?: string): GridColDef[] {
	return [
		ID_GRID_COLDEF,
		{
			field: "callType",
			headerName: "Call Type",
			flex: 1,
			sortable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader params={params} />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => {
				let content = params.formattedValue ?? params.value;
				if (isFooterCell(params)) {
					content = percents?.callType ?? "Unattended Mishandled Call (%)";
				}
				return (
					<TypographyCell params={params} issues={issues} reference={issuesReference}>
						<Stack direction={"row"} style={{ alignItems: "center" }}>
							{content}
							{params.row.hasTooltip && (
								<Box style={{ paddingLeft: "8px" }}>
									<Tooltip title={params?.row?.tooltip} placement="right" arrow>
										<img src={InfoIcon} alt="Information icon" className="size-[15px]" />
									</Tooltip>
								</Box>
							)}
						</Stack>
					</TypographyCell>
				);
			},
		},
		{
			field: "queue",
			headerName: "Queue",
			type: "number",
			align: "right",
			maxWidth: 90,
			sortable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader params={params} right />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => {
				let content = params.formattedValue ?? params.value;
				if (isFooterCell(params)) {
					let amount = percents?.queue ?? 0;
					amount = getFormatAmount(amount);
					content = amount.toString() + "%";
				}
				return (
					<TypographyCell params={params} issues={issues} reference={issuesReference}>
						{isEmptyRow(params) ? "" : content}
					</TypographyCell>
				);
			},
			valueFormatter: (value) => {
				return parseInt(value, 10);
			},
		},
		{
			field: "individual",
			headerName: "Individual",
			type: "number",
			align: "right",
			maxWidth: 90,
			sortable: false,
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader params={params} right />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => {
				let content = params.formattedValue ?? params.value;
				if (isFooterCell(params)) {
					let amount = percents?.individual ?? 0;
					amount = getFormatAmount(amount);
					content = amount.toString() + "%";
				}
				return (
					<TypographyCell params={params} issues={issues} reference={issuesReference}>
						{isEmptyRow(params) ? "" : content}
					</TypographyCell>
				);
			},
			valueFormatter: (value) => {
				return parseInt(value, 10);
			},
		},
		{
			field: "total",
			headerName: "Total",
			type: "number",
			maxWidth: 90,
			sortable: false,
			align: "right",
			headerClassName: HEADER_CLASS_NAME,
			renderHeader: (params) => <TypographyHeader params={params} right />,
			cellClassName: getCellClassNameFn(issues),
			renderCell: (params) => {
				let content = params.formattedValue ?? params.value;
				if (isFooterCell(params)) {
					let amount = percents?.total ?? 0;
					amount = getFormatAmount(amount);
					content = amount.toString() + "%";
				}
				return (
					<TypographyCell params={params} issues={issues} reference={issuesReference}>
						{isEmptyRow(params) ? "" : content}
					</TypographyCell>
				);
			},
			valueFormatter: (value) => {
				return parseInt(value, 10);
			},
		},
	];
}

function formatTurnAroundTime(value: number): string {
	const secondsInHour = 3600;
	const hours = Math.floor(value / secondsInHour);
	const minutes = Math.floor((value % secondsInHour) / 60);
	let text = `${hours}hr`;
	if (hours !== 1) {
		text += "s";
	}
	if (minutes > 0) {
		text += ` ${minutes}m`;
	}
	return `Avg. Turnaround Time: ${text}`;
}

function addTooltipToRows(rows: GridValidRowModel[]): GridValidRowModel[] {
	const outboundToMishandledCalls = rows.find((row) => String(row.callType).toUpperCase() === "OUTBOUND TO MISHANDLED CALLS");
	if (outboundToMishandledCalls) {
		outboundToMishandledCalls.hasTooltip = true;
		outboundToMishandledCalls.tooltip = formatTurnAroundTime(outboundToMishandledCalls.turnAroundTime);
	}
	return rows;
}

function ChargesTable(props: Readonly<CallActivityPerformanceProps>) {
	const title = props.title ?? "Call Activity Performance";
	const apiRef = useGridApiRef();
	const [totalColumns, setTotalColumns] = useState<GridColDef[]>([]);
	const [rows, setRows] = useState<GridValidRowModel[]>([]);

	// Issues support attributes
	const [openAddIssues, setOpenAddIssues] = useState(false);
	const [anchorAddIssues, setAnchorAddIssues] = useState<(HTMLDivElement & EventTarget) | null>(null);
	const [issues, setIssues] = useState<Issue[]>([]);
	const activeIssue = useRef<Issue>(null);

	const handleClickCell: MouseEventHandler<HTMLDivElement> = (event) => {
		activeIssue.current = {
			rowReference: event.currentTarget.parentElement?.dataset.id, // _id
			columnReference: event.currentTarget.dataset.field, // field
		};
		setAnchorAddIssues(event.currentTarget);
		setOpenAddIssues(true);
	};

	const handleChangeIssues = (color: string | null) => {
		let currentIssues = [...issues];
		if (color) {
			let isPresent = false;
			for (const issue of currentIssues) {
				if (
					issue &&
					issue.rowReference === activeIssue?.current?.rowReference &&
					issue.columnReference === activeIssue?.current?.columnReference
				) {
					issue.color = color;
					isPresent = true;
					break;
				}
			}

			if (!isPresent) {
				currentIssues.push({
					color,
					tableReference: props?.issuesReference,
					rowReference: activeIssue?.current?.rowReference,
					columnReference: activeIssue?.current?.columnReference,
				});
			}
		} else {
			currentIssues?.forEach((issue, key) => {
				if (
					issue &&
					issue.rowReference === activeIssue?.current?.rowReference &&
					issue.columnReference === activeIssue?.current?.columnReference
				) {
					currentIssues[key] = null;
				}
			});
			currentIssues = currentIssues.filter((issue) => issue);
		}

		setIssues(currentIssues);
		props?.onIssuesUpdatedAction?.(currentIssues, props?.issuesReference);
	};

	useMemo(() => {
		setTotalColumns(getColumns(issues, props?.percents, props?.issuesReference));
	}, [props?.issuesReference, props?.percents, issues]);

	useEffect(() => {
		// Clear filter on purpose
		try {
			const filter = apiRef.current?.state?.filter;
			if (filter) {
				const cleanFilter = {
					filterModel: { items: [], linkOperator: "and" },
					visibleRows: null,
					visibleRowsLookup: {},
				};

				apiRef.current.setState((prevState) => ({
					...prevState,
					filter: { ...filter, ...cleanFilter },
				}));
			}
		} catch (error) {
			errorLogger(getError(error));
		}

		setIssues(props.issues ?? []);
		setRows(addTooltipToRows(props?.data ?? []));

		return () => {
			setTotalColumns([]);
			setRows([]);

			// Issues controls
			setIssues([]);
			activeIssue.current = null;
			setOpenAddIssues(false);
			setAnchorAddIssues(null);
		};
	}, [props, apiRef]);

	if (!totalColumns) {
		return null;
	}

	function getCell(): Partial<GridCellProps> | undefined {
		if (props?.issues) {
			return { onClick: handleClickCell };
		}
	}

	return (
		<>
			<DataSmartTable
				apiRef={apiRef}
				columns={totalColumns}
				rows={rows}
				disableColumnResize
				hideFooter
				disableColumnMenu
				disableRowSelectionOnClick
				disableMultipleRowSelection
				checkboxSelection={false}
				columnVisibilityModel={{
					_id: false,
				}}
				initialState={{
					aggregation: {
						model: {
							total: "sum",
							individual: "sum",
							queue: "sum",
						},
					},
				}}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					toolbar: {
						customTitle: {
							hide: false,
							label: title,
							sx: { mb: "15px" },
						},
					},
					noRowsOverlay: {
						title: `No ${title} Data`,
					},
					cell: getCell(),
				}}
				getCellClassName={(params) => {
					// Targeting footer
					if (params.id === "auto-generated-group-footer-root") {
						// Use this to change the cell class name depending on the cell value
						const negatives = ["total"];
						if (negatives.includes(params.field) && typeof params.value === "number" && params.value < 0) {
							return "aggregation-footer-negative";
						}

						// Append label 'Total' to the cell value in footer
						if (params.field === "callType") {
							return "aggregation-footer-total";
						}
					}
					return "";
				}}
				customProps={{}}
				{...props.tableProps}
			/>
			{props.addIssues && (
				<AddCellIssues
					open={openAddIssues}
					anchorEl={anchorAddIssues}
					onCloseAction={() => {
						setOpenAddIssues(false);
						setAnchorAddIssues(null);
					}}
					onChangeAction={handleChangeIssues}
				/>
			)}
		</>
	);
}

export default function CallActivityPerformance(props: Readonly<CallActivityPerformanceProps>) {
	if (props.loading) {
		return <TableLoader smallTitle content />;
	}

	return <>{props.dataPresent && <ChargesTable {...props} />}</>;
}
